import { useEffect, useMemo, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import api from '../../services/api';
import HrEIDCardWidget from './HrEIDCardWidget';
import "../../assets/styles/HR/BulkPg.css";

function downloadTextAsCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DemoCsvTable({ rows }) {
  if (!rows?.length) return null;
  const [headers, ...data] = rows;
  return (
    <div className="bulkpg-inline-1">
      <table className="tbl bulkpg-inline-2">
        <thead>
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, idx) =>
          <tr key={idx}>
              {headers.map((_, cIdx) =>
            <td key={cIdx} className="bulkpg-inline-3">{row[cIdx] || ''}</td>
            )}
            </tr>
          )}
        </tbody>
      </table>
    </div>);

}

const formatDateOnly = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getLifecycleStatusMeta = (status) => {
  const key = String(status || '').toLowerCase();
  const map = {
    pending_pm: { label: 'Pending PM', chip: 'chip-ip' },
    pending_hr: { label: 'Pending HR', chip: 'chip-ip' },
    approved_pm: { label: 'Approved PM', chip: 'chip-ok' },
    approved_hr: { label: 'Approved HR', chip: 'chip-ok' },
    rejected_pm: { label: 'Rejected PM', chip: 'chip-dn' },
    rejected_hr: { label: 'Rejected HR', chip: 'chip-dn' },
    resend_hr: { label: 'Resend To HR', chip: 'chip-wn' },
    resend_employee: { label: 'Resend To Employee', chip: 'chip-wn' },
    pm_scheduled: { label: 'PM Scheduled', chip: 'chip-wn' },
    notice_period: { label: 'Notice Period', chip: 'chip-wn' },
    awaiting_employee_form: { label: 'Awaiting Employee Form', chip: 'chip-wn' },
    pending_it: { label: 'Pending IT', chip: 'chip-wn' },
    it_replied: { label: 'IT Replied', chip: 'chip-wn' },
    hr_docs_uploaded: { label: 'HR Docs Uploaded', chip: 'chip-wn' },
    settlement_pending_pm: { label: 'Settlement Pending PM', chip: 'chip-wn' },
    settlement_rework_hr: { label: 'Settlement Rework HR', chip: 'chip-dn' },
    settlement_approved_pm: { label: 'Settlement Approved PM', chip: 'chip-ok' },
    settlement_sent: { label: 'Settlement Sent', chip: 'chip-ok' }
  };
  return map[key] || { label: status || '-', chip: 'chip-wn' };
};

function BulkPg() {
  const [tab, setTab] = useState('users');
  const [suc, setSuc] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const usersFileRef = useRef(null);
  const calendarFileRef = useRef(null);
  const attFileRef = useRef(null);

  const [usersResult, setUsersResult] = useState(null);
  const [eidPreviewId, setEidPreviewId] = useState(null);
  const [calResult, setCalResult] = useState(null);
  const [showUsersDemo, setShowUsersDemo] = useState(false);
  const [showCalDemo, setShowCalDemo] = useState(false);
  const [showAttDemo, setShowAttDemo] = useState(false);
  const [showAttPmDemo, setShowAttPmDemo] = useState(false);
  const [attResult, setAttResult] = useState(null);
  const [pmOptions, setPmOptions] = useState([]);
  const [attSendForm, setAttSendForm] = useState({ title: '', pmId: '', comment: '', file: null });
  const [onboardingForm, setOnboardingForm] = useState({ title: '', pmId: '', comment: '', file: null });
  const [offboardingForm, setOffboardingForm] = useState({ title: '', pmId: '', userId: '', comment: '', file: null });
  const [offboardEmpOptions, setOffboardEmpOptions] = useState([]);
  const [offInitLoading, setOffInitLoading] = useState(false);
  const [publishForm, setPublishForm] = useState({ file: null });
  const [liveInstruction, setLiveInstruction] = useState(null);
  const [onboardingRows, setOnboardingRows] = useState([]);
  const [offboardingRows, setOffboardingRows] = useState([]);
  const [activeLogReqId, setActiveLogReqId] = useState('');
  const [activeLogs, setActiveLogs] = useState([]);
  const [salaryData, setSalaryData] = useState('');
  const [statusData, setStatusData] = useState('');
  const [docEmpId, setDocEmpId] = useState('');
  const [docs, setDocs] = useState([]);
  const [payslipForm, setPayslipForm] = useState({ title: '', comment: '', file: null });
  const [internCertForm, setInternCertForm] = useState({ title: '', comment: '', file: null });
  const [payrollForm, setPayrollForm] = useState({ title: '', comment: '', month: '', year: '', file: null });
  const [activePayslipTemplate, setActivePayslipTemplate] = useState(null);
  const [activeInternCertTemplate, setActiveInternCertTemplate] = useState(null);
  const [showPayrollDemo, setShowPayrollDemo] = useState(false);
  const [payrollProcessResult, setPayrollProcessResult] = useState(null);
  const [payrollLogs, setPayrollLogs] = useState([]);

  const usersSampleRows = useMemo(() => [
  ['fullName', 'dob', 'bloodGroup', 'gender', 'maritalStatus', 'phone', 'email', 'panNumber', 'passportNumber', 'passportIssueDate', 'passportExpiryDate', 'passportIssuePlace', 'drivingLicenseNumber', 'drivingLicenseExpiry', 'currentAddress', 'currentCity', 'currentState', 'currentPincode', 'permanentAddress', 'permanentCity', 'permanentState', 'permanentPincode', 'bankName', 'accountNo', 'ifsc', 'branch', 'firstName', 'lastName', 'fatherName', 'offerLetterDate', 'offerAcceptedDate', 'expectedJoiningDate', 'education', 'declaration', 'declarationPlace', 'declarationDate', 'aadhaar', 'pan', 'photo', 'drivingLicense', 'passport', 'marksheet10', 'marksheet12', 'role', 'department', 'designation', 'manager_emp_id', 'college', 'start_date', 'end_date', 'employee_type'],
  ['Arun Kumar', '1999-08-15', 'B+', 'Male', 'Single', '9876543210', 'arun.kumar@company.com', 'ABCDE1234F', 'A1234567', '2020-01-10', '2030-01-09', 'Chennai', 'TN0120230001234', '2032-12-31', '12 MG Road', 'Chennai', 'Tamil Nadu', '600001', '12 MG Road', 'Chennai', 'Tamil Nadu', '600001', 'State Bank of India', '123456789012', 'SBIN0000456', 'T Nagar', 'Arun', 'Kumar', 'Ravi Kumar', '2026-01-10', '2026-01-15', '2026-02-01', '[{"degree":"B.Tech","specialization":"CSE","institution":"Anna University","boardUniversity":"Anna University","score":"8.4","scoreType":"cgpa","startMonth":"June","startYear":"2017","endMonth":"May","endYear":"2021","isPursuing":false}]', 'TRUE', 'Chennai', '2026-02-21', 'aadhaar.pdf', 'pan.pdf', 'photo.jpg', 'driving_license.pdf', 'passport.pdf', 'marksheet10.pdf', 'marksheet12.pdf', 'employee', 'Engineering', 'Software Engineer', 'PM-2026-1001', '', '', '', 'Full-time']],
  []);

  const usersSampleCsv = useMemo(
    () => usersSampleRows.map((r) => r.join(',')).join('\n'),
    [usersSampleRows]
  );

  const calSampleRows = useMemo(() => [
  ['title', 'date', 'end_date', 'type', 'color', 'description', 'scope'],
  ['Company All Hands', '2026-03-10', '2026-03-10', 'event', '#4338ca', 'Monthly company-wide all hands meeting', 'global'],
  ['Founders Day', '2026-03-21', '2026-03-21', 'holiday', '#ef4444', 'Company holiday for all employees', 'global']],
  []);

  const calSampleCsv = useMemo(
    () => calSampleRows.map((r) => r.join(',')).join('\n'),
    [calSampleRows]
  );

  const attendanceSampleRows = useMemo(() => [
  ['emp_id', 'date', 'status', 'check_in', 'check_out'],
  ['EMP-2024-1456', '2026-02-10', 'present', '09:00', '18:00'],
  ['EMP-2024-1457', '2026-02-10', 'absent', '', ''],
  ['EMP-2024-1458', '2026-02-10', 'wfh', '09:30', '17:30']],
  []);

  const attendanceSampleCsv = useMemo(
    () => attendanceSampleRows.map((r) => r.join(',')).join('\n'),
    [attendanceSampleRows]
  );

  const pmAttendanceSampleRows = useMemo(() => [
  ['Emp ID', 'Name', 'Role', 'Dept', 'Status', 'Check In', 'Check Out', 'Hours'],
  ['EMP-2024-1456', 'Arun Kumar', 'employee', 'Engineering', 'present', '09:00', '18:00', '9.0'],
  ['EMP-2024-1457', 'Priya N', 'intern', 'Engineering', 'wfh', '09:30', '17:30', '8.0']],
  []);

  const pmAttendanceSampleCsv = useMemo(
    () => pmAttendanceSampleRows.map((r) => r.join(',')).join('\n'),
    [pmAttendanceSampleRows]
  );

  const payrollSampleRows = useMemo(() => [
  ['S NO', 'Emp Code', 'Emp Name', 'Designation', 'Dept (Team)', 'Payable days', 'Absent / LOP', 'Net paid days', 'Gross Pay (Actuals)', 'CTC (Actuals)', 'Basic Salary', 'HRA', 'Bonus', 'Other Allowance', 'Stipend', 'Reimbursement', 'Other Earnings', 'Incentive', 'Gross Earning', 'PF', 'ESI', 'Profession Tax', 'Advance', 'TDS', 'Gross Deduction', 'Pension', 'EPF', 'Total Employee PF', 'Total Employee ESI', 'Gratuity', 'Total CTC', 'Month', 'Year'],
  ['1', 'EMP-2024-1456', 'Arun Kumar', 'Software Engineer', 'Engineering', '30', '1', '29', '65000', '80000', '30000', '12000', '4000', '3000', '0', '0', '2500', '2000', '53500', '1800', '500', '200', '0', '1000', '3500', '0', '1800', '1800', '500', '1000', '80000', 'February', '2026'],
  ['2', 'EMP-2024-1457', 'Priya N', 'Frontend Intern', 'Engineering', '30', '0', '30', '42000', '50000', '18000', '6000', '2000', '1500', '0', '0', '1000', '500', '29000', '900', '250', '200', '0', '400', '1750', '0', '900', '900', '250', '0', '50000', 'February', '2026']],
  []);

  const payrollSampleCsv = useMemo(
    () => payrollSampleRows.map((r) => r.join(',')).join('\n'),
    [payrollSampleRows]
  );

  const internCertTemplateHtml = useMemo(() => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Internship Certificate</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;600&display=swap');
    body { margin:0; background:#f3f4f6; font-family:'Inter', sans-serif; }
    .page { max-width:900px; margin:30px auto; background:#fff; border:2px solid #0f172a; padding:50px; position:relative; }
    .frame { position:absolute; inset:12px; border:1px solid #e2e8f0; pointer-events:none; }
    .header { text-align:center; }
    .logo { height:56px; margin-bottom:8px; }
    .company { font-family:'Playfair Display', serif; font-size:26px; color:#0f172a; letter-spacing:1px; }
    .tagline { font-size:11px; color:#64748b; letter-spacing:2px; margin-top:2px; }
    .title { margin:28px 0 10px; font-family:'Playfair Display', serif; font-size:34px; color:#0f172a; letter-spacing:2px; }
    .body { text-align:center; font-size:15px; line-height:1.9; color:#334155; margin:18px 40px 0; }
    .name { font-size:26px; font-family:'Playfair Display', serif; color:#0f172a; font-weight:700; border-bottom:2px solid #0f172a; display:inline-block; padding-bottom:2px; }
    .footer { display:flex; justify-content:space-between; margin-top:48px; padding:0 40px; font-size:12px; color:#64748b; }
    .sig { text-align:center; }
    .sig .line { width:160px; border-bottom:1px solid #334155; margin:0 auto 6px; }
  </style>
</head>
<body>
  <div class="page">
    <div class="frame"></div>
    <div class="header">
      <img class="logo" src="{{company_logo_url}}" alt="Company Logo" />
      <div class="company">{{company_name}}</div>
      <div class="tagline">{{company_tagline}}</div>
      <div class="title">CERTIFICATE OF INTERNSHIP</div>
    </div>
    <div class="body">
      This is to certify that<br />
      <span class="name">{{intern_name}}</span><br />
      has successfully completed their internship as<br />
      <b>{{role}}</b> in the <b>{{department}}</b> department<br />
      from <b>{{start_date}}</b> to <b>{{end_date}}</b><br />
      under the supervision of <b>{{manager}}</b> and <b>{{pm}}</b>.
    </div>
    <div class="footer">
      <div class="sig"><div class="line"></div>HR Manager</div>
      <div class="sig"><div class="line"></div>Date: {{issue_date}}</div>
      <div class="sig"><div class="line"></div>Project Manager</div>
    </div>
  </div>
</body>
</html>`, []);

  useEffect(() => {
    if (!['attendance', 'onboardingFile', 'payroll'].includes(tab) || pmOptions.length > 0) return;
    api.getPMs().then((rows) => setPmOptions(Array.isArray(rows) ? rows : [])).catch(() => setPmOptions([]));
  }, [tab, pmOptions.length]);

  useEffect(() => {
    if (tab === 'offboardingFile') setTab('onboardingFile');
    if (tab !== 'offboardingFile') return;
    api.getHrPeople().
    then((rows) => {
      const list = (Array.isArray(rows) ? rows : []).
      filter((r) => ['employee', 'intern'].includes(String(r.role || '').toLowerCase())).
      map((r) => ({
        user_id: r.id,
        userId: r.id,
        id: r.id,
        emp_id: r.emp_id || '',
        name: r.name || r.full_name || '',
        role: r.designation || r.role || '',
        designation: r.designation || '',
        dept: r.department || '',
        department_name: r.department || ''
      })).
      sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      setOffboardEmpOptions(list);
    }).
    catch(() => setOffboardEmpOptions([]));
  }, [tab]);

  const loadLifecycleRows = async (requestType) => {
    try {
      const rows = await api.getLifecycleHrRequests(requestType);
      if (requestType === 'onboarding') setOnboardingRows(Array.isArray(rows) ? rows : []);
      if (requestType === 'offboarding') setOffboardingRows(Array.isArray(rows) ? rows : []);
    } catch {
      if (requestType === 'onboarding') setOnboardingRows([]);
      if (requestType === 'offboarding') setOffboardingRows([]);
    }
  };

  const loadLiveInstruction = async () => {
    try {
      const row = await api.getActiveOnboardingInstruction();
      setLiveInstruction(row || null);
    } catch {
      setLiveInstruction(null);
    }
  };

  useEffect(() => {
    if (tab === 'onboardingFile') loadLifecycleRows('onboarding');
    if (tab === 'offboardingFile') loadLifecycleRows('offboarding');
    if (tab === 'onboardingFile') loadLiveInstruction();
    if (tab === 'payslip' || tab === 'payroll') {
      api.getActivePayslipTemplate().then((d) => setActivePayslipTemplate(d || null)).catch(() => setActivePayslipTemplate(null));
      if (tab === 'payroll') {
        api.getPayrollProcessLogs().then((rows) => setPayrollLogs(Array.isArray(rows) ? rows : [])).catch(() => setPayrollLogs([]));
      }
    }
    if (tab === 'internCertificate') {
      api.getActiveInternCertificateTemplate().then((d) => setActiveInternCertTemplate(d || null)).catch(() => setActiveInternCertTemplate(null));
    }
  }, [tab]);

  const flashSuccess = (msg) => {setSuc(msg);setTimeout(() => setSuc(''), 7000);};

  const handleUsersTemplateDownload = async () => {
    try {
      const csvText = await api.downloadBulkUsersTemplate();
      downloadTextAsCsv('bulk-users-template.csv', csvText);
    } catch {
      downloadTextAsCsv('bulk-users-template.csv', usersSampleCsv);
    }
  };

  const handleCalendarTemplateDownload = async () => {
    try {
      const csvText = await api.downloadCalendarTemplate();
      downloadTextAsCsv('calendar-events-template.csv', csvText);
    } catch {
      downloadTextAsCsv('calendar-events-template.csv', calSampleCsv);
    }
  };

  const handleUsersUpload = async () => {
    const file = usersFileRef.current?.files?.[0];
    if (!file) return setErr('Please select a users CSV file');
    setLoading(true);
    setErr('');
    setUsersResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.bulkUploadUsers(fd);
      setUsersResult(res);
      const mailFail = Number(res.emailFailedCount || 0);
      const managerMiss = Number(res.managerUnresolvedCount || 0);
      flashSuccess(
        `Users upload complete: ${res.createdCount || 0} created, ${res.errorCount || 0} failed` +
        (mailFail > 0 ? `, ${mailFail} email delivery failed` : '') +
        (managerMiss > 0 ? `, ${managerMiss} manager mappings missing` : '')
      );
      if (mailFail > 0 || managerMiss > 0) {
        const firstFail = Array.isArray(res.emailFailures) && res.emailFailures.length ? res.emailFailures[0] : null;
        const firstManagerMiss = Array.isArray(res.managerUnresolved) && res.managerUnresolved.length ? res.managerUnresolved[0] : null;
        const reasonCode = firstFail?.code ? ` (${firstFail.code})` : '';
        const reasonMsg = firstFail?.message ? ` Reason: ${firstFail.message}` : '';
        const emailMsg = mailFail > 0
          ? `${mailFail} credential email(s) failed${reasonCode}. Share Emp ID/password manually or re-send via Credentials update.${reasonMsg}`
          : '';
        const managerMsg = managerMiss > 0
          ? `${managerMiss} row(s) manager map ஆகலை. Check manager_emp_id/manager_email/manager_name.`
            + (firstManagerMiss?.managerInput ? ` First unresolved manager input: ${firstManagerMiss.managerInput}` : '')
          : '';
        setErr([emailMsg, managerMsg].filter(Boolean).join(' '));
      }
    } catch (e) {
      setErr('Users upload failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleCalendarUpload = async () => {
    const file = calendarFileRef.current?.files?.[0];
    if (!file) return setErr('Please select an events CSV file');
    setLoading(true);
    setErr('');
    setCalResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.bulkUploadCalendarEvents(fd);
      setCalResult(res);
      flashSuccess(`Calendar upload complete: ${res.insertedCount || 0} events created, ${res.errorCount || 0} failed`);
    } catch (e) {
      setErr('Calendar upload failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleAttUpload = async () => {
    const file = attFileRef.current?.files?.[0];
    if (!file) return setErr('Please select a CSV file');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadAttendanceCsv(fd);
      setAttResult(res);
      flashSuccess(`Attendance uploaded: ${res.inserted} inserted, ${res.absent || 0} marked absent`);
    } catch (e) {
      setErr('Upload failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleAttendanceSampleDownload = () => {
    downloadTextAsCsv('attendance-upload-template.csv', attendanceSampleCsv);
  };

  const handleSendAttendanceToPm = async () => {
    if (!attSendForm.pmId || !attSendForm.file) return setErr('Select PM and file to send');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('title', attSendForm.title || 'Attendance Report');
      fd.append('pmId', String(attSendForm.pmId));
      fd.append('projectId', '');
      fd.append('reportType', 'Attendance');
      fd.append('comment', attSendForm.comment || '');
      fd.append('file', attSendForm.file);
      await api.uploadHrReport(fd);
      setAttSendForm({ title: '', pmId: '', comment: '', file: null });
      flashSuccess('Attendance file sent to PM. It will appear in PM Dashboard > HR Reports');
    } catch (e) {
      setErr('Send failed: ' + e.message);
    }
    setLoading(false);
  };

  const handlePmAttendanceSampleDownload = () => {
    downloadTextAsCsv('attendance-report-for-pm-template.csv', pmAttendanceSampleCsv);
  };

  const handleSendLifecycleToPm = async (requestType) => {
    const form = requestType === 'onboarding' ? onboardingForm : offboardingForm;
    const setForm = requestType === 'onboarding' ? setOnboardingForm : setOffboardingForm;
    if (!form.pmId || !form.file) return setErr('Select PM and file to send');
    if (requestType === 'offboarding' && !form.userId) return setErr('Select employee for offboarding file');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('requestType', requestType);
      fd.append('title', form.title || `${requestType} file`);
      fd.append('pmId', String(form.pmId));
      if (requestType === 'offboarding') fd.append('userId', String(form.userId));
      fd.append('comment', form.comment || '');
      fd.append('file', form.file);
      await api.createLifecycleHrRequest(fd);
      setForm(requestType === 'offboarding' ? { title: '', pmId: '', userId: '', comment: '', file: null } : { title: '', pmId: '', comment: '', file: null });
      flashSuccess(`${requestType} file sent to manager successfully`);
      await loadLifecycleRows(requestType);
    } catch (e) {
      setErr('Send failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleInitiateOffboarding = async () => {
    if (!offboardingForm.userId) return setErr('Select employee to proceed');
    setOffInitLoading(true);
    setErr('');
    try {
      await api.initiateHrOffboarding({
        userId: Number(offboardingForm.userId),
        pmId: offboardingForm.pmId ? Number(offboardingForm.pmId) : undefined,
        title: 'Offboarding Initiated',
        comment: offboardingForm.comment || 'HR initiated offboarding process. Fill offboarding form.'
      });
      flashSuccess('Proceed completed. Employee notified and Offboarding next step activated.');
      await loadLifecycleRows('offboarding');
    } catch (e) {
      setErr('Proceed failed: ' + e.message);
    }
    setOffInitLoading(false);
  };

  const selectedOffboardEmployee = useMemo(() => {
    if (!offboardingForm.userId) return null;
    return offboardEmpOptions.find((emp) => String(emp.user_id || emp.userId || emp.id) === String(offboardingForm.userId)) || null;
  }, [offboardingForm.userId, offboardEmpOptions]);

  const handlePublishOnboardingInstruction = async () => {
    if (!publishForm.file) return setErr('Please select a file to publish');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', publishForm.file);
      await api.publishOnboardingInstruction(fd);
      setPublishForm({ file: null });
      flashSuccess('Live onboarding instruction updated. Upcoming users will see this latest file.');
      await loadLiveInstruction();
      await loadLifecycleRows('onboarding');
    } catch (e) {
      setErr('Publish failed: ' + e.message);
    }
    setLoading(false);
  };

  const openLogs = async (reqId) => {
    try {
      const rows = await api.getLifecycleLogs(reqId);
      setActiveLogReqId(reqId);
      setActiveLogs(Array.isArray(rows) ? rows : []);
    } catch {
      setActiveLogReqId(reqId);
      setActiveLogs([]);
    }
  };

  const handleSalaryRevision = async () => {
    setLoading(true);
    setErr('');
    try {
      const lines = salaryData.trim().split('\n').filter((l) => l.trim());
      if (lines.length < 2) throw new Error('Need header + at least 1 row');
      const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const revisions = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c) => c.trim());
        const obj = {};
        header.forEach((h, idx) => {obj[h] = Number.isNaN(Number(cols[idx])) ? cols[idx] : Number(cols[idx]);});
        revisions.push(obj);
      }
      const res = await api.bulkSalaryRevision(revisions);
      setSalaryData('');
      flashSuccess(`Salary revision: ${res.updated} records updated`);
    } catch (e) {
      setErr('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleStatusUpdate = async () => {
    setLoading(true);
    setErr('');
    try {
      const lines = statusData.trim().split('\n').filter((l) => l.trim());
      if (lines.length < 2) throw new Error('Need header + at least 1 row');
      const updates = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c) => c.trim());
        updates.push({ emp_id: cols[0], status: cols[1] || 'ACTIVE' });
      }
      const res = await api.bulkStatusUpdate(updates);
      setStatusData('');
      flashSuccess(`Status update: ${res.updated} users updated`);
    } catch (e) {
      setErr('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleDocSearch = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await api.bulkDocDownload(docEmpId);
      setDocs(res || []);
      if (!res?.length) setErr('No documents found');
    } catch (e) {
      setErr('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await api.getDocUrl(doc.id);
      if (res?.url) await api.downloadFile(res.url, res.name || doc.doc_name);else
      setErr('Download not available');
    } catch (e) {
      setErr('Download failed: ' + e.message);
    }
  };

  const handlePayrollSampleDownload = () => {
    downloadTextAsCsv('payroll-upload-template.csv', payrollSampleCsv);
  };

  const handleInternCertSampleDownload = () => {
    const blob = new Blob([internCertTemplateHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'internship-certificate-template.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadPayslipTemplate = async () => {
    if (!payslipForm.file) return setErr('Select payslip template file');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('title', payslipForm.title || 'Payslip Base Template');
      fd.append('comment', payslipForm.comment || '');
      fd.append('file', payslipForm.file);
      await api.uploadPayslipTemplate(fd);
      const active = await api.getActivePayslipTemplate().catch(() => null);
      setActivePayslipTemplate(active || null);
      setPayslipForm({ title: '', comment: '', file: null });
      flashSuccess('Payslip template uploaded as active template');
    } catch (e) {
      setErr('Template upload failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleUploadInternCertificateTemplate = async () => {
    if (!internCertForm.file) return setErr('Select intern certificate template file');
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('title', internCertForm.title || 'Internship Certificate Template');
      fd.append('comment', internCertForm.comment || '');
      fd.append('file', internCertForm.file);
      await api.uploadInternCertificateTemplate(fd);
      const active = await api.getActiveInternCertificateTemplate().catch(() => null);
      setActiveInternCertTemplate(active || null);
      setInternCertForm({ title: '', comment: '', file: null });
      flashSuccess('Intern certificate template uploaded as active template');
    } catch (e) {
      setErr('Template upload failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleDirectPayrollUpload = async () => {
    if (!payrollForm.file) return setErr('Please select a payroll CSV file');
    if (!payrollForm.file.name.toLowerCase().endsWith('.csv')) return setErr('Only CSV files are supported. Export Excel as CSV first.');
    setLoading(true);
    setErr('');
    setPayrollProcessResult(null);
    try {
      const fd = new FormData();
      fd.append('file', payrollForm.file);
      if (payrollForm.month)   fd.append('month', payrollForm.month);
      if (payrollForm.year)    fd.append('year',  payrollForm.year);
      if (payrollForm.title)   fd.append('title', payrollForm.title);
      if (payrollForm.comment) fd.append('comment', payrollForm.comment);
      const res = await api.processPayrollUpload(fd);
      setPayrollProcessResult(res || null);
      setPayrollForm({ title: '', comment: '', month: '', year: '', file: null });
      const logs = await api.getPayrollProcessLogs().catch(() => []);
      setPayrollLogs(Array.isArray(logs) ? logs : []);
      flashSuccess('Payroll processed: ' + (res.updated || 0) + ' payslips generated, ' + (res.skipped || 0) + ' skipped.');
    } catch (e) {
      setErr('Payroll processing failed: ' + e.message);
    }
    setLoading(false);
  };



  const downloadPayrollRunLogPdf = (log) => {
    const doc = new jsPDF();
    const skipped = Array.isArray(log?.skipped) ? log.skipped : [];
    let y = 16;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Payroll Process Log', 14, y);
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Run ID: ${log?.run_id || '-'}`, 14, y);y += 6;
    doc.text(`Month/Year: ${(log?.month_label || '-') + ' / ' + (log?.year_value || '-')}`, 14, y);y += 6;
    doc.text(`Source File: ${log?.source_file_name || '-'}`, 14, y);y += 6;
    doc.text(`Template: ${log?.template_file_name || '-'}`, 14, y);y += 6;
    doc.text(`Updated: ${log?.updated_count || 0} | Skipped: ${log?.skipped_count || 0}`, 14, y);y += 6;
    doc.text(`Processed By: ${log?.processed_by_name || '-'} | Date: ${log?.created_at ? new Date(log.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-'}`, 14, y);y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Skipped Rows', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');

    if (!skipped.length) {
      doc.text('No skipped rows', 14, y);
    } else {
      skipped.slice(0, 120).forEach((s, idx) => {
        if (y > 280) {
          doc.addPage();
          y = 18;
        }
        const line = `${idx + 1}. ${s.empId ? `${s.empId} - ` : ''}${s.reason || 'Skipped'}`;
        doc.text(line, 14, y);
        y += 5;
      });
    }

    doc.save(`payroll-log-${log?.run_id || Date.now()}.pdf`);
  };

  const downloadAllPayrollLogsPdf = () => {
    if (!payrollLogs.length) return;
    const doc = new jsPDF();
    let y = 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Payroll Processing History', 14, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`, 14, y);
    y += 10;

    payrollLogs.forEach((log, idx) => {
      if (y > 278) {
        doc.addPage();
        y = 18;
      }
      doc.text(
        `${idx + 1}. ${log.run_id || '-'} | ${(log.month_label || '-') + ' ' + (log.year_value || '')} | Updated ${log.updated_count || 0} | Skipped ${log.skipped_count || 0}`,
        14,
        y
      );
      y += 6;
    });

    doc.save(`payroll-process-history-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const tabs = [
  ['users', 'Add Users'],
  ['calendar', 'Calendar Events'],
  ['attendance', 'Attendance Upload'],
  ['onboardingFile', 'Onboarding Upload'],
  ['payslip', 'Payslip'],
  ['internCertificate', 'Intern Certificate'],
  ['payroll', 'Payroll']];


  const step3Done = !!payrollProcessResult?.runId;

  return (
    <div>
      <div className="sec">Uploads & Bulk Operations</div>
      {suc && <div className="bulkpg-inline-4">OK {suc}</div>}
      {err && <div className="bulkpg-inline-5">ERR {err}</div>}

      <div className="bulkpg-inline-6">
        {tabs.map(([k, l]) =>
        <button key={k} className={`tab-btn${tab === k ? ' active' : ''}`} onClick={() => {setTab(k);setErr('');}}>
            {l}
          </button>
        )}
      </div>

      {tab === 'users' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Add Users</div>
            <div className="cd-s">Upload CSV to create employee/intern accounts with temp credentials and email notification</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-7">
              <button className="btn-o" onClick={() => setShowUsersDemo((p) => !p)}>
                {showUsersDemo ? 'Hide Demo File' : 'View Demo File'}
              </button>
              <button className="btn-o" onClick={handleUsersTemplateDownload}>Download Sample CSV</button>
            </div>
            {showUsersDemo && <DemoCsvTable rows={usersSampleRows} />}
            <input ref={usersFileRef} type="file" accept=".csv,.txt" className="fi bulkpg-inline-8" />
            <button className="btn btn-p" onClick={handleUsersUpload} disabled={loading}>{loading ? 'Uploading...' : 'Upload Users CSV'}</button>
            {usersResult &&
          <div className="bulkpg-inline-9">
                Created/Updated: <b>{usersResult.createdCount || 0}</b> | Existing Updated: <b>{usersResult.updatedExistingCount || 0}</b> | Failed: <b>{usersResult.errorCount || 0}</b>
              </div>
          }
            {/* EID Card Previews for newly created employees */}
            {usersResult?.created?.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0a2244", marginBottom: 10 }}>
                  🪪 EID Card Preview — {usersResult.created.length} Employee{usersResult.created.length !== 1 ? "s" : ""} Created
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                  {usersResult.created.map((emp) => (
                    <button
                      key={emp.emp_id || emp.userId}
                      onClick={() => setEidPreviewId(emp.emp_id || emp.userId)}
                      style={{
                        padding: "6px 14px", borderRadius: 20,
                        border: `1.5px solid ${eidPreviewId === (emp.emp_id || emp.userId) ? "#1a5fa8" : "#e2e8f0"}`,
                        background: eidPreviewId === (emp.emp_id || emp.userId) ? "#1a5fa8" : "#fff",
                        color: eidPreviewId === (emp.emp_id || emp.userId) ? "#fff" : "#0a2244",
                        fontSize: 11, fontWeight: 600, cursor: "pointer"
                      }}>
                      🪪 {emp.name || emp.emp_id}
                    </button>
                  ))}
                  {eidPreviewId && (
                    <button
                      onClick={() => setEidPreviewId(null)}
                      style={{
                        padding: "6px 14px", borderRadius: 20,
                        border: "1.5px solid #e2e8f0", background: "#fff",
                        color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer"
                      }}>
                      ✕ Close Preview
                    </button>
                  )}
                </div>
                {eidPreviewId && (
                  <div style={{ background: "#f8faff", borderRadius: 14, padding: 20, border: "1px solid #e0edff" }}>
                    <HrEIDCardWidget
                      empId={eidPreviewId}
                      onClose={() => setEidPreviewId(null)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }

      {tab === 'calendar' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Calendar Events CSV Upload</div>
            <div className="cd-s">Uploaded events become global and visible to all users, with notification + email</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-10">
              <button className="btn-o" onClick={() => setShowCalDemo((p) => !p)}>
                {showCalDemo ? 'Hide Demo File' : 'View Demo File'}
              </button>
              <button className="btn-o" onClick={handleCalendarTemplateDownload}>Download Sample CSV</button>
            </div>
            {showCalDemo && <DemoCsvTable rows={calSampleRows} />}
            <input ref={calendarFileRef} type="file" accept=".csv,.txt" className="fi bulkpg-inline-11" />
            <button className="btn btn-p" onClick={handleCalendarUpload} disabled={loading}>{loading ? 'Uploading...' : 'Upload Events CSV'}</button>
            {calResult &&
          <div className="bulkpg-inline-12">
                Inserted: <b>{calResult.insertedCount || 0}</b> | Failed: <b>{calResult.errorCount || 0}</b>
              </div>
          }
          </div>
        </div>
      }

      {tab === 'attendance' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Bulk Attendance Upload</div>
            <div className="cd-s">CSV: emp_id,date,status,check_in,check_out</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-13">
              <button className="btn-o" onClick={() => setShowAttDemo((p) => !p)}>
                {showAttDemo ? 'Hide Demo File' : 'View Demo File'}
              </button>
              <button className="btn-o" onClick={handleAttendanceSampleDownload}>Download Sample CSV</button>
            </div>
            {showAttDemo && <DemoCsvTable rows={attendanceSampleRows} />}
            <input ref={attFileRef} type="file" accept=".csv,.txt" className="fi bulkpg-inline-14" />
            <button className="btn btn-p" onClick={handleAttUpload} disabled={loading}>{loading ? 'Uploading...' : 'Upload CSV'}</button>
            {attResult && <div className="bulkpg-inline-15">Inserted: <b>{attResult.inserted}</b> | Absent: <b>{attResult.absent || 0}</b> | Errors: <b>{attResult.errors}</b></div>}

            <div className="bulkpg-inline-16">
              <div className="bulkpg-inline-17">Send Attendance File To PM (HR Reports)</div>
              <div className="bulkpg-inline-18">
                <button className="btn-o" onClick={() => setShowAttPmDemo((p) => !p)}>
                  {showAttPmDemo ? 'Hide PM Sample File' : 'View PM Sample File'}
                </button>
                <button className="btn-o" onClick={handlePmAttendanceSampleDownload}>Download PM Sample CSV</button>
              </div>
              {showAttPmDemo && <DemoCsvTable rows={pmAttendanceSampleRows} />}
              <div className="bulkpg-inline-19">
                <input
                className="fi bulkpg-inline-20"
                placeholder="Title"
                value={attSendForm.title}
                onChange={(e) => setAttSendForm((p) => ({ ...p, title: e.target.value }))} />

              
                <select
                className="fi bulkpg-inline-21"
                value={attSendForm.pmId}
                onChange={(e) => setAttSendForm((p) => ({ ...p, pmId: e.target.value }))}>

                
                  <option value="">Select PM</option>
                  {pmOptions.map((pm) => <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>)}
                </select>
              </div>
              <textarea
              className="fta bulkpg-inline-22"
              rows={3}
              placeholder="Comment"
              value={attSendForm.comment}
              onChange={(e) => setAttSendForm((p) => ({ ...p, comment: e.target.value }))} />

            
              <input type="file" onChange={(e) => setAttSendForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="bulkpg-inline-23" />
              <button className="btn" onClick={handleSendAttendanceToPm} disabled={loading || !attSendForm.pmId || !attSendForm.file}>
                Send To PM
              </button>
            </div>
          </div>
        </div>
      }

      {tab === 'payslip' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Payslip Template</div>
            <div className="cd-s">Upload base payslip template used for payroll processing</div>
          </div>
          <div className="cd-b">
            <input className="fi" placeholder="Template Title" value={payslipForm.title} onChange={(e) => setPayslipForm((p) => ({ ...p, title: e.target.value }))} />
            <textarea className="fta bulkpg-inline-24" rows={3} placeholder="Comment" value={payslipForm.comment} onChange={(e) => setPayslipForm((p) => ({ ...p, comment: e.target.value }))} />
            <input type="file" onChange={(e) => setPayslipForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="bulkpg-inline-25" />
            <button className="btn btn-p" onClick={handleUploadPayslipTemplate} disabled={loading || !payslipForm.file}>{loading ? 'Uploading...' : 'Upload Template'}</button>
            {activePayslipTemplate &&
          <div className="bulkpg-inline-26">
                <div><b>Active Template:</b> {activePayslipTemplate.file_name || '-'}</div>
                <div><b>Updated:</b> {activePayslipTemplate.created_at ? new Date(activePayslipTemplate.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</div>
                <div className="bulkpg-inline-27">
<button className="btn-o btn-s" onClick={() => {
                    if (!activePayslipTemplate?.file_url) { alert('No file uploaded yet. Please upload a payslip template first.'); return; }
                    api.downloadFile(activePayslipTemplate.file_url, activePayslipTemplate.file_name || 'payslip-template');
                  }}>Download</button>                </div>
              </div>
          }
          </div>
        </div>
      }

      {tab === 'internCertificate' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Internship Certificate Template</div>
            <div className="cd-s">Upload an HTML/TXT template. Placeholders will be replaced with intern data on download.</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-10">
              <button className="btn-o" onClick={handleInternCertSampleDownload}>Download Sample Template</button>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
              Use placeholders like <code>{'{{intern_name}}'}</code>, <code>{'{{intern_id}}'}</code>, <code>{'{{role}}'}</code>, <code>{'{{department}}'}</code>, <code>{'{{manager}}'}</code>, <code>{'{{pm}}'}</code>, <code>{'{{start_date}}'}</code>, <code>{'{{end_date}}'}</code>, <code>{'{{issue_date}}'}</code>, <code>{'{{company_name}}'}</code>.
            </div>
            <input className="fi" placeholder="Template Title" value={internCertForm.title} onChange={(e) => setInternCertForm((p) => ({ ...p, title: e.target.value }))} />
            <textarea className="fta bulkpg-inline-24" rows={3} placeholder="Comment" value={internCertForm.comment} onChange={(e) => setInternCertForm((p) => ({ ...p, comment: e.target.value }))} />
            <input type="file" accept=".html,.htm,.txt" onChange={(e) => setInternCertForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="bulkpg-inline-25" />
            <button className="btn btn-p" onClick={handleUploadInternCertificateTemplate} disabled={loading || !internCertForm.file}>{loading ? 'Uploading...' : 'Upload Template'}</button>
            {activeInternCertTemplate &&
          <div className="bulkpg-inline-26">
                <div><b>Active Template:</b> {activeInternCertTemplate.file_name || '-'}</div>
                <div><b>Updated:</b> {activeInternCertTemplate.created_at ? new Date(activeInternCertTemplate.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</div>
                <div className="bulkpg-inline-27">
 <button className="btn-o btn-s" onClick={() => {
                    if (!activeInternCertTemplate?.file_url) { alert('No file uploaded yet. Please upload an intern certificate template first.'); return; }
                    api.downloadFile(activeInternCertTemplate.file_url, activeInternCertTemplate.file_name || 'intern-certificate-template');
                  }}>Download</button>                </div>
              </div>
          }
          </div>
        </div>
      }

      {tab === 'payroll' && (
        <div>
          {/* Success result banner */}
          {payrollProcessResult && (
            <div style={{background:'#f0fdf4',border:'2px solid #bbf7d0',borderRadius:12,padding:'14px 18px',
              marginBottom:16,display:'flex',gap:16,alignItems:'center'}}>
              <span style={{fontSize:26}}>✅</span>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:'#15803d'}}>Payroll Processed Successfully</div>
                <div style={{fontSize:12,color:'#166534',marginTop:3}}>
                  <b>{payrollProcessResult.updated||0}</b> payslips generated &nbsp;·&nbsp;
                  <b>{(payrollProcessResult.skipped||[]).length||payrollProcessResult.skipped||0}</b> skipped &nbsp;·&nbsp;
                  Employees can now view payslips in their portal
                </div>
              </div>
              <button onClick={()=>setPayrollProcessResult(null)}
                style={{marginLeft:'auto',background:'none',border:'none',fontSize:16,cursor:'pointer',color:'#94a3b8'}}>✕</button>
            </div>
          )}

          {/* Step 1 — Template */}
          <div className="cd mb16">
            <div className="cd-h">
              <div className="cd-t">Step 1 — Download Payroll Template</div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn-o btn-s" onClick={() => setShowPayrollDemo(p=>!p)}>
                  {showPayrollDemo ? 'Hide Preview' : 'Preview Template'}
                </button>
                <button className="btn-o btn-s" onClick={handlePayrollSampleDownload}>
                  ⬇ Download CSV Template
                </button>
              </div>
            </div>
            {showPayrollDemo && <div className="cd-b"><DemoCsvTable rows={payrollSampleRows} /></div>}
            <div className="cd-b" style={{paddingTop:showPayrollDemo?0:undefined}}>
              <div style={{fontSize:12,color:'#64748b',lineHeight:1.7}}>
                Fill employee payroll data using the template. Required columns:
                <span style={{fontFamily:'monospace',fontSize:11,background:'#f1f5f9',
                  padding:'2px 6px',borderRadius:4,marginLeft:4}}>
                  Emp Code, Basic Salary, HRA, Gross Earning, PF, ESI, Month, Year
                </span>
                &nbsp;&nbsp;<b style={{color:'#dc2626'}}>⚠ Excel (.xlsx) not supported</b> — Export as CSV.
              </div>
            </div>
          </div>

          {/* Step 2 — Direct Upload */}
          <div className="cd mb16">
            <div className="cd-h" style={{background:'linear-gradient(135deg,#1e1b4b,#4f46e5)'}}>
              <div className="cd-t" style={{color:'#fff'}}>Step 2 — Upload & Process Payroll</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,.75)',fontWeight:600}}>
                Direct — No PM Approval Needed
              </div>
            </div>
            <div className="cd-b">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                <div className="fg">
                  <label className="fl">Payroll CSV File *</label>
                  <input type="file" accept=".csv,.txt" className="fi"
                    onChange={e=>setPayrollForm(p=>({...p,file:e.target.files?.[0]||null}))} />
                  {payrollForm.file && !payrollForm.file.name.toLowerCase().endsWith('.csv') && (
                    <div style={{color:'#dc2626',fontSize:11,marginTop:3}}>CSV only — export Excel as CSV first</div>
                  )}
                </div>
                <div className="fg">
                  <label className="fl">Title (optional)</label>
                  <input className="fi" placeholder="e.g. March 2026 Payroll"
                    value={payrollForm.title} onChange={e=>setPayrollForm(p=>({...p,title:e.target.value}))} />
                </div>
                <div className="fg">
                  <label className="fl">Month (optional)</label>
                  <select className="fi" value={payrollForm.month}
                    onChange={e=>setPayrollForm(p=>({...p,month:e.target.value}))}>
                    <option value="">Auto-detect from CSV</option>
                    {['January','February','March','April','May','June',
                      'July','August','September','October','November','December'].map(m=>
                      <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="fg">
                  <label className="fl">Year (optional)</label>
                  <select className="fi" value={payrollForm.year}
                    onChange={e=>setPayrollForm(p=>({...p,year:e.target.value}))}>
                    <option value="">Auto-detect from CSV</option>
                    {[2026,2025,2024].map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <textarea className="fta" rows={2} placeholder="Notes (optional)"
                value={payrollForm.comment} onChange={e=>setPayrollForm(p=>({...p,comment:e.target.value}))} />
              <div style={{marginTop:12}}>
                <button className="btn" onClick={handleDirectPayrollUpload}
                  disabled={loading || !payrollForm.file}>
                  {loading ? 'Processing...' : 'Upload & Generate Payslips'}
                </button>
                <span style={{fontSize:11,color:'#94a3b8',marginLeft:12}}>
                  Employees will see payslips immediately after processing
                </span>
              </div>
            </div>
          </div>

          {/* Step 3 — Logs */}
          <div className="cd">
            <div className="cd-h">
              <div className="cd-t">Payroll Process Logs</div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <span className="cd-s">{payrollLogs.length} runs</span>
                <button className="btn-o btn-s" disabled={!payrollLogs.length}
                  onClick={downloadAllPayrollLogsPdf}>PDF All</button>
              </div>
            </div>
            <div className="cd-b">
              {payrollLogs.length === 0 ? (
                <div style={{textAlign:'center',padding:24,color:'#94a3b8',fontSize:13}}>
                  No payroll runs yet. Upload a CSV to get started.
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Run ID</th><th>Source File</th><th>Month/Year</th>
                      <th>Updated</th><th>Skipped</th><th>Processed By</th><th>Date</th><th>Log</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollLogs.map(log => (
                      <tr key={log.id||log.run_id}>
                        <td style={{fontWeight:600,fontSize:11,color:'#4f46e5'}}>{log.run_id}</td>
                        <td style={{fontSize:11}}>{log.source_file_name||'—'}</td>
                        <td style={{fontSize:11}}>{(log.month_label||'—')+' '+(log.year_value||'')}</td>
                        <td><span style={{fontWeight:700,color:'#16a34a'}}>{log.updated_count||0}</span></td>
                        <td><span style={{fontWeight:700,color:log.skipped_count>0?'#d97706':'#94a3b8'}}>{log.skipped_count||0}</span></td>
                        <td style={{fontSize:11}}>{log.processed_by_name||'—'}</td>
                        <td style={{fontSize:10,color:'#94a3b8'}}>{log.created_at?new Date(log.created_at).toLocaleDateString('en-IN'):'—'}</td>
                        <td><button className="btn-o btn-s" onClick={()=>downloadPayrollRunLogPdf(log)}>PDF</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'onboardingFile' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Onboarding File Upload</div>
            <div className="cd-s">Step 1: Send file to PM for review | Step 2: Publish final live file (no further approval)</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-50">
              <input className="fi bulkpg-inline-51" placeholder="Title" value={onboardingForm.title} onChange={(e) => setOnboardingForm((p) => ({ ...p, title: e.target.value }))} />
              <select className="fi bulkpg-inline-52" value={onboardingForm.pmId} onChange={(e) => setOnboardingForm((p) => ({ ...p, pmId: e.target.value }))}>
                <option value="">Select PM</option>
                {pmOptions.map((pm) => <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>)}
              </select>
            </div>
            <textarea className="fta bulkpg-inline-53" rows={3} placeholder="Comment" value={onboardingForm.comment} onChange={(e) => setOnboardingForm((p) => ({ ...p, comment: e.target.value }))} />
            <input type="file" onChange={(e) => setOnboardingForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="bulkpg-inline-54" />
            <button className="btn" disabled={loading || !onboardingForm.pmId || !onboardingForm.file} onClick={() => handleSendLifecycleToPm('onboarding')}>
              {loading ? 'Sending...' : 'Send To Manager'}
            </button>

            <div className="bulkpg-inline-55">
              <div className="bulkpg-inline-56">Publish Latest Onboarding Instruction (Live for Upcoming Users)</div>
              {liveInstruction ?
            <div className="bulkpg-inline-57">
                  <div><b>Current Live:</b> {liveInstruction.file_name || 'onboarding-instruction'}</div>
                  <div><b>Updated:</b> {new Date(liveInstruction.updated_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</div>
                  {liveInstruction.req_id ? <div><b>Source Req:</b> {liveInstruction.req_id}</div> : null}
                  <div className="bulkpg-inline-58">
                    <button className="btn-o btn-s" onClick={() => {
                      if (!liveInstruction.file_url) { alert('No file available to download.'); return; }
                      api.downloadFile(liveInstruction.file_url, liveInstruction.file_name || 'onboarding-instruction');
                    }}>Download Live File</button>                 
                 </div>
                </div> :

            <div className="bulkpg-inline-59">No live onboarding instruction published yet.</div>
            }
              <div className="bulkpg-inline-60">
                <input type="file" onChange={(e) => setPublishForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
              </div>
              <button className="btn" disabled={loading || !publishForm.file} onClick={handlePublishOnboardingInstruction}>
                {loading ? 'Uploading...' : 'Upload As Live Instruction'}
              </button>
            </div>

            <div className="bulkpg-inline-61">
              <table className="tbl bulkpg-inline-62">
                <thead>
                  <tr>
                    <th>Req ID</th><th>Title</th><th>Status</th><th>Manager</th><th>File</th><th>Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {onboardingRows.length === 0 ?
                <tr><td colSpan="6" className="bulkpg-inline-63">No onboarding upload logs</td></tr> :
                onboardingRows.map((r) =>
                <tr key={r.req_id}>
                      <td className="bulkpg-inline-64">{r.req_id}</td>
                      <td>{r.title || '-'}</td>
                      <td><span className={`chip ${getLifecycleStatusMeta(r.status).chip}`}>{getLifecycleStatusMeta(r.status).label}</span></td>
                      <td>{r.pm_name || '-'}</td>
                      <td>{r.file_url ? <button className="btn-o btn-s" onClick={() => api.downloadFile(r.file_url, r.file_name || 'onboarding-file')}>Download</button> : '-'}</td>
                      <td><button className="btn-o btn-s" onClick={() => openLogs(r.req_id)}>View</button></td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }

      {tab === 'offboardingFile' &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Offboarding File Upload</div>
            <div className="cd-s">Select employee -> view employee widget -> proceed. Then upload/send manager flow.</div>
          </div>
          <div className="cd-b">
            <div className="bulkpg-inline-65">
              <div className="bulkpg-inline-66">Initiate Offboarding (Helpdesk -> HR Proceed)</div>
              <div className="bulkpg-inline-67">
                <select className="fi bulkpg-inline-68" value={offboardingForm.userId} onChange={(e) => setOffboardingForm((p) => ({ ...p, userId: e.target.value }))}>
                  <option value="">Select Employee</option>
                  {offboardEmpOptions.map((emp) => <option key={emp.user_id || emp.userId || emp.id} value={emp.user_id || emp.userId}>{emp.name} ({emp.emp_id || emp.id})</option>)}
                </select>
                <select className="fi bulkpg-inline-69" value={offboardingForm.pmId} onChange={(e) => setOffboardingForm((p) => ({ ...p, pmId: e.target.value }))}>
                  <option value="">Select PM (optional)</option>
                  {pmOptions.map((pm) => <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>)}
                </select>
              </div>
              {selectedOffboardEmployee ?
            <div className="bulkpg-inline-70">
                  <div><b>Name:</b> {selectedOffboardEmployee.name || '-'}</div>
                  <div><b>Emp ID:</b> {selectedOffboardEmployee.emp_id || selectedOffboardEmployee.id || '-'}</div>
                  <div><b>Designation:</b> {selectedOffboardEmployee.role || selectedOffboardEmployee.designation || '-'}</div>
                  <div><b>Department:</b> {selectedOffboardEmployee.dept || selectedOffboardEmployee.department_name || '-'}</div>
                </div> :
            null}
              <button className="btn" onClick={handleInitiateOffboarding} disabled={offInitLoading || !offboardingForm.userId}>
                {offInitLoading ? 'Proceeding...' : 'Proceed'}
              </button>
            </div>

            <div className="bulkpg-inline-71">
              <input className="fi bulkpg-inline-72" placeholder="Title" value={offboardingForm.title} onChange={(e) => setOffboardingForm((p) => ({ ...p, title: e.target.value }))} />
              <select className="fi bulkpg-inline-73" value={offboardingForm.pmId} onChange={(e) => setOffboardingForm((p) => ({ ...p, pmId: e.target.value }))}>
                <option value="">Select PM</option>
                {pmOptions.map((pm) => <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>)}
              </select>
              <select className="fi bulkpg-inline-74" value={offboardingForm.userId} onChange={(e) => setOffboardingForm((p) => ({ ...p, userId: e.target.value }))}>
                <option value="">Select Employee</option>
                {offboardEmpOptions.map((emp) => <option key={emp.user_id || emp.userId || emp.id} value={emp.user_id || emp.userId}>{emp.name} ({emp.emp_id || emp.id})</option>)}
              </select>
            </div>
            <textarea className="fta bulkpg-inline-75" rows={3} placeholder="Comment" value={offboardingForm.comment} onChange={(e) => setOffboardingForm((p) => ({ ...p, comment: e.target.value }))} />
            <input type="file" onChange={(e) => setOffboardingForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} className="bulkpg-inline-76" />
            <button className="btn" disabled={loading || !offboardingForm.pmId || !offboardingForm.userId || !offboardingForm.file} onClick={() => handleSendLifecycleToPm('offboarding')}>
              {loading ? 'Sending...' : 'Send To Manager'}
            </button>

            <div className="bulkpg-inline-77">
              <table className="tbl bulkpg-inline-78">
                <thead>
                  <tr>
                    <th>Req ID</th><th>Title</th><th>Employee</th><th>Status</th><th>Manager</th><th>File</th><th>Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {offboardingRows.length === 0 ?
                <tr><td colSpan="7" className="bulkpg-inline-79">No offboarding upload logs</td></tr> :
                offboardingRows.map((r) =>
                <tr key={r.req_id}>
                      <td className="bulkpg-inline-80">{r.req_id}</td>
                      <td>{r.title || '-'}</td>
                      <td>{r.user_name || '-'}</td>
                      <td><span className={`chip ${getLifecycleStatusMeta(r.status).chip}`}>{getLifecycleStatusMeta(r.status).label}</span></td>
                      <td>{r.pm_name || '-'}</td>
                      <td>{r.file_url ? <button className="btn-o btn-s" onClick={() => api.downloadFile(r.file_url, r.file_name || 'offboarding-file')}>Download</button> : '-'}</td>
                      <td><button className="btn-o btn-s" onClick={() => openLogs(r.req_id)}>View</button></td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }

      {(tab === 'onboardingFile' || tab === 'offboardingFile') && activeLogReqId &&
      <div className="cd bulkpg-inline-81">
          <div className="cd-h">
            <div className="cd-t">Logs - {activeLogReqId}</div>
            <div className="cd-s">{activeLogs.length} entries</div>
          </div>
          <div className="cd-b">
            {activeLogs.length === 0 ? <div className="bulkpg-inline-82">No logs</div> : activeLogs.map((l) =>
          <div key={l.id} className="bulkpg-inline-83">
                <div className="bulkpg-inline-84">{l.action}</div>
                <div className="bulkpg-inline-85">{l.comment || '-'}</div>
                <div className="bulkpg-inline-86">{formatDateOnly(l.created_at)}</div>
              </div>
          )}
          </div>
        </div>
      }

    </div>);

}

export default BulkPg;
